import React, { FC, useEffect, useState } from "react";
import { Icon, Image } from "@vacasa/react-components-lib";
import { useFormValue } from "../../../hooks/FormValue";
import BasicButton from "../../../elements/Button/BasicButton";
import FormInput from "../../../elements/Form";
import { useDebounce } from "../../../hooks/Debounce";

import "./AddAlertSubscriber.scss";
import SmartDeviceService from "../../../services/smart-device.service";
import {
  AddAlertSubscriberRequest,
  AlertSubscriber,
  Login,
  Subscriber,
} from "../../../models/SmartDeviceApi";
import { CanceledError } from "axios";
import { PropertyStore } from "../../../store/property";
import { AppContextStore } from "../../../store/app-context";

interface AddAlertSubscriberProps {
  existingSubscribers?: AlertSubscriber[];
  onSubmit?: (data: AlertSubscriber) => void;
  allTypes?: boolean;
}

const AddAlertSubscriber: FC<AddAlertSubscriberProps> = (props) => {
  const property = PropertyStore.selectors.usePropertyData()!;

  const [isSaving, setIsSaving] = useState(false);
  const [apiSaveError, setApiSaveError] = useState<string>();

  const [isFormValid, setIsFormValid] = useState(false);
  const username = useFormValue<string>("", true);
  const fullName = useFormValue<string>("", true);
  const phone = useFormValue<string>("", true);
  const debouncedUsername = useDebounce(username.value, 500);
  const debouncedPhone = useDebounce(phone.value, 500);
  const debouncedFullName = useDebounce(fullName.value, 500);
  const [methodEmail, setMethodEmail] = useState(false);
  const [methodSms, setMethodSms] = useState(false);
  const [subscriptionNoise, setSubscriptionNoise] = useState(false);
  const [subscriptionTemperature, setSubscriptionTemperature] = useState(false);
  const [subscriptionRouter, setSubscriptionRouter] = useState(false);

  const [userData, setUserData] = useState<Subscriber>();
  const [isDuplicateUser, setIsDuplicateUser] = useState(false);
  const [isUserDataLoading, setIsUserDataLoading] = useState(false);
  const [userDataError, setUserDataError] = useState<string>();
  const [isNewSubscriber, setIsNewSubscriber] = useState(false);
  const origin = AppContextStore.selectors.useAppOrigin();
  const originFromAdmin = origin === "admin" ? true : false;

  // Loads user based on username
  useEffect(() => {
    const abortController = new AbortController();
    const service = new SmartDeviceService();

    const trimmed = debouncedUsername?.trim();

    // Validación de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = trimmed && emailRegex.test(trimmed);

    if (
      trimmed &&
      trimmed[0] !== "." &&
      trimmed[trimmed.length - 1] !== "." &&
      isValidEmail
    ) {
      setIsUserDataLoading(true);
      setUserDataError(undefined);
      // Search email in subscribers and login (only if originFromAdmin)
      const promises: Promise<Subscriber[] | Login | null>[] = [
        service.getSubscribersByEmail(`${trimmed}`),
        ...(originFromAdmin
          ? [
              service.getLoginByEmailAddress(
                `${trimmed}`,
                abortController.signal,
              ),
            ]
          : []),
      ];

      Promise.allSettled(promises)
        .then((results) => {
          const subscribersResult = results[0] as PromiseSettledResult<
            Subscriber[]
          >;
          const loginResult = originFromAdmin
            ? (results[1] as PromiseSettledResult<Login | null>)
            : undefined;

          // Use subscriber data if available
          if (
            subscribersResult.status === "fulfilled" &&
            subscribersResult.value &&
            subscribersResult.value.length > 0
          ) {
            setIsNewSubscriber(false);
            if (!originFromAdmin) subscribersResult.value[0].LoginId = null;
            setUserData(subscribersResult.value[0]);
          }
          // If no subscribers but login exists, use login (only if originFromAdmin)
          else if (
            originFromAdmin &&
            loginResult &&
            loginResult.status === "fulfilled" &&
            loginResult.value
          ) {
            setIsNewSubscriber(true);
            setUserData({
              Email: loginResult.value.EmailAddress ?? "",
              FullName: `${loginResult.value.FirstName} ${loginResult.value.LastName}`,
              Phone: loginResult.value.Phone ?? "",
              LoginId: loginResult.value.Id ?? null,
              UpdatedDate: null,
              SubscriberId: "",
            });
            phone.onChange(loginResult.value.Phone ?? "", true);
            fullName.onChange(
              `${loginResult.value.FirstName} ${loginResult.value.LastName}`,
              true,
            );
          } else if (
            subscribersResult.status === "rejected" &&
            (!originFromAdmin ||
              (loginResult && loginResult.status === "rejected"))
          ) {
            setUserDataError(
              (subscribersResult.reason as any)?.response?.error,
            );
          } else {
            setIsNewSubscriber(true);
            setUserData({
              Email: username.value ?? "",
              FullName: "",
              Phone: "",
              LoginId: null,
              UpdatedDate: null,
              SubscriberId: "",
            });
            phone.onChange("", true);
            fullName.onChange("", true);
          }
        })
        .catch((err) => {
          if (!(err instanceof CanceledError)) {
            if (err.response && err.response.error) {
              setUserDataError(err.response.error);
            } else {
              setUserDataError(err.message);
            }
          }
        })
        .finally(() => setIsUserDataLoading(false));
    } else if (trimmed && !isValidEmail) {
      // Email no válido
      setIsUserDataLoading(false);
      setUserDataError("Please enter a valid email address");
    } else {
      setIsUserDataLoading(false);
      setUserDataError(undefined);
    }

    return () => {
      abortController.abort();
    };
  }, [debouncedUsername]);

  useEffect(() => {
    const userPhone = debouncedPhone?.trim();
    if (userData) {
      userData["Phone"] = userPhone ?? "";
    }
  }, [debouncedPhone]);

  useEffect(() => {
    const userFullName = debouncedFullName?.trim();
    if (userData) {
      userData["FullName"] = userFullName ?? "";
    }
  }, [debouncedFullName]);

  // Validates form
  useEffect(() => {
    const atLeastOneMethod = methodSms || methodEmail;
    const atLeastOneSubscription =
      subscriptionNoise || subscriptionTemperature || subscriptionRouter;

    // Para nuevos suscriptores, validar que phone y fullName no estén vacíos
    const isNewSubscriberValid = isNewSubscriber
      ? !!fullName.value?.trim()
      : true;

    setIsFormValid(
      !!userData &&
        atLeastOneMethod &&
        atLeastOneSubscription &&
        !isDuplicateUser &&
        isNewSubscriberValid,
    );
  }, [
    userData,
    methodEmail,
    methodSms,
    subscriptionNoise,
    subscriptionTemperature,
    subscriptionRouter,
    isDuplicateUser,
    isNewSubscriber,
    phone.value,
    fullName.value,
  ]);

  // Checks whether user is a duplicate subscriber
  useEffect(() => {
    if (!userData) {
      setIsDuplicateUser(false);
      return;
    }

    const index = props.existingSubscribers?.findIndex(
      (x) => x.SubscriberId === userData.SubscriberId,
    );
    setIsDuplicateUser(index !== undefined && index >= 0);
  }, [userData, props.existingSubscribers]);

  const handleSmsChange = (e: any) => setMethodSms(e.target.checked);
  const handleEmailChange = (e: any) => setMethodEmail(e.target.checked);
  const toggleNoise = () => setSubscriptionNoise(!subscriptionNoise);
  const toggleTemperature = () =>
    setSubscriptionTemperature(!subscriptionTemperature);
  const toggleRouter = () => setSubscriptionRouter(!subscriptionRouter);

  // Parses form and submits data
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!isFormValid) return;

    try {
      setIsSaving(true);
      setApiSaveError(undefined);

      // Create subscriber if it doesn't exist
      if (isNewSubscriber && userData) {
        const subscriberInfo = await addSubscriber(userData);
        if (subscriberInfo) {
          userData.SubscriberId = subscriberInfo.SubscriberId;
        }
      }

      await addAlertSubscriber();
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setApiSaveError((error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const addSubscriber = async (data: Subscriber) => {
    const service = new SmartDeviceService();
    const result = await service.addSubscriber(data);
    return result;
  };

  const addAlertSubscriber = async () => {
    if (!userData) {
      throw new Error("User data is required");
    }

    const service = new SmartDeviceService();
    const payload: AddAlertSubscriberRequest = {
      DeviceGroup: property.id,
      LoginId: userData.LoginId,
      SubscriberId: userData.SubscriberId ?? "",
      SendEmail: methodEmail,
      SendSms: methodSms,
      SensorNoiseAlert: subscriptionNoise,
      SensorTemperatureAlert: subscriptionTemperature,
      RouterConnectedAlert: subscriptionRouter,
    };
    const result = await service.addAlertSubscriber(payload);
    props.onSubmit?.(result);
    return result;
  };

  const subscriptionsDiv = () => {
    return (
      <div className="subscribe-channel">
        <div className="label-title">Subscriptions</div>
        <div
          className={`badge SensorNoiseAlert ${
            subscriptionNoise ? "checked" : "unchecked"
          }`}
          onClick={toggleNoise}
        >
          <span className="icon">
            <Icon.Radio />
          </span>
          <span className="title">Noise Sensor</span>
        </div>
        <div
          className={`badge SensorTemperatureAlert ${
            subscriptionTemperature ? "checked" : "unchecked"
          }`}
          onClick={toggleTemperature}
        >
          <span className="icon">
            <Icon.Thermometer />
          </span>
          <span className="title">Temperature</span>
        </div>
        {props.allTypes ? (
          <div
            className={`badge RouterConnectedAlert ${
              subscriptionRouter ? "checked" : "unchecked"
            }`}
            onClick={toggleRouter}
          >
            <span className="icon">
              <Icon.Wifi />
            </span>
            <span className="title">Router</span>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  };

  const getUserDetails = () => {
    if (!username.value) return undefined;
    else if (isUserDataLoading) {
      return (
        <div>
          <Image.Spinner width={36} height={36} />
        </div>
      );
    } else if (userDataError) {
      return (
        <div className="no-user">
          <span className="error-message">{userDataError}</span>
        </div>
      );
    } else if (!userData || isNewSubscriber) {
      return (
        <div className="add-user">
          <div className="subscribe-method">
            <div className="inline-input">
              <FormInput.Text
                label="Full Name"
                value={fullName}
                width={150}
                disabled={isSaving}
              />
            </div>
            <div className="inline-input">
              <input
                type="checkbox"
                checked={methodSms}
                onChange={handleSmsChange}
                disabled={isSaving}
              />
              <FormInput.Text
                label="Phone"
                value={phone}
                width={150}
                disabled={isSaving}
              />
            </div>
            <div className="inline-input">
              <input
                type="checkbox"
                checked={methodEmail}
                onChange={handleEmailChange}
                disabled={isSaving}
              />
              <span className="email">{username.value}</span>
            </div>
          </div>
          {subscriptionsDiv()}
        </div>
      );
    } else if (isDuplicateUser) {
      return (
        <div className="user-detail">
          <div className="subscribe-method">
            <div className="name"> {userData.FullName} </div>
            <div className="no-user">
              <div>This user is already set up as a subscriber.</div>
              <div>
                If you&apos;d like to update the alerts for this user, please
                edit the existing subscriber.
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="user-detail">
          <div className="subscribe-method">
            <div className="name"> {userData.FullName} </div>
            <div>
              <input
                type="checkbox"
                checked={methodSms}
                onChange={handleSmsChange}
                disabled={isSaving}
              />
              <span className="phone">{userData.Phone}</span>
            </div>
            <div>
              <input
                type="checkbox"
                checked={methodEmail}
                onChange={handleEmailChange}
                disabled={isSaving}
              />
              <span className="email">{userData.Email}</span>
            </div>
          </div>
          {subscriptionsDiv()}
        </div>
      );
    }
  };

  return (
    <div className="component-add-alert-subscriber">
      <div className="component-title">Add Alert Subscriber</div>
      <form onSubmit={handleSubmit}>
        <div className="input-username">
          <FormInput.Text
            label="Email"
            value={username}
            width={150}
            disabled={isSaving}
          />
        </div>
        {getUserDetails()}
        <div className="footer">
          <BasicButton
            type="submit"
            disabled={!isFormValid || isSaving}
            display="fill"
          >
            Add Alert Subscriber
          </BasicButton>
          {isSaving && (
            <span className="spinner">
              <Image.Spinner height={36} width={36} />
            </span>
          )}
          <div className="error-message">{apiSaveError}</div>
        </div>
      </form>
    </div>
  );
};

export default AddAlertSubscriber;
